import React from 'react';
import { get } from 'lodash-es';
import { VehiclePreloader } from 'site-modules/shared/utils/core-page/vehicle-preloader-v2';
import {
  ConsumerReviewsModel,
  ConsumerReviewsPaths,
  DEFAULT_PAGE_NUM,
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORTING,
  DEFAULT_RATING,
  SORT_BY,
} from 'client/data/models/consumer-reviews';
import { URL_CONTEXT_PATH, UrlModel } from 'client/data/models/url';
import { getModelState } from 'client/data/luckdragon/model';
import { getSeoUrlsByLocation } from 'site-modules/consumer-reviews/utils/seo-url-builder';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { create404Error } from 'site-modules/shared/utils/errors';
import {
  buildPopularStylesPath,
  buildUsedTmvPricingPath,
  buildVehiclePath,
  buildVehicleStylePath,
  getUsedBaseStylePath,
  buildMakeModelDefaultYear,
  VehicleModel,
  FeatureSpecsPaths,
} from 'client/data/models/vehicle-v2';
import { VisitorModel } from 'client/data/models/visitor';
import { buildRecommendedTrimPath, buildReviewPath, EditorialReviewModel } from 'client/data/models/editorial-review';
import { buildAllPhotosPath, PhotoflipperModel } from 'client/data/models/photoflipper';
import { InventoryModel, InventoryPaths } from 'client/data/models/inventory';
import { isPreProd, isUsed as isUsedPubState } from 'site-modules/shared/utils/publication-states';
import {
  buildSpecificStyleDisplayVehicle,
  buildDisplayVehicle,
} from 'site-modules/shared/utils/core-page/display-name-builders';
import { LinkWidgetsModel, getPageLinksPath } from 'client/data/models/link-widgets';
import { buildCoreBreadcrumbs, getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { getCoreUrlPattern } from 'site-modules/shared/utils/core-page/url-pattern';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { getCorePageCarSchema } from 'client/seo/schemas/car';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { legacyPageUpdate } from 'client/actions/legacy-page';
import { setCriticalCss } from 'client/actions/critical-css';
import { INCENTIVES_AD, BUILD_PRICE_AD } from 'client/site-modules/shared/components/native-ad/utils/constants';
import { setAdsListToPreload } from 'client/site-modules/shared/components/native-ad/utils/utils';
import { getQuery } from 'client/utils/location';
import { getParamsWithMakes } from 'client/utils/router-params';
import {
  getInitialSearchFilter,
  getUnknownLocationSearchFilter,
  MINI_INVENTORY_COUNT,
} from 'site-modules/shared/utils/mini-inventory/mini-inventory';
import { getFilterFromLocation } from 'site-modules/shared/utils/inventory/search-filter';
import { PageModel } from 'client/data/models/page';
import {
  buildTrimSummariesPath,
  InventoryCoreConfigurationsModel,
} from 'client/data/models/inventory-core-configurations';
import { buildMMYSubmodelTypeAmountsPath, InventoryAggregateModel } from 'client/data/models/inventory-aggregate';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { ConsumerReviews, PAGE_DISPLAY_NAME, URL_PATH } from './consumer-reviews';

const PAGE_NAME = 'model_core_consumer_reviews';
const PAGE_CATEGORY = 'model_core';

export const pageOptions = {
  name: PAGE_NAME,
  category: PAGE_CATEGORY,
  component: props => <ConsumerReviews {...props} params={getParamsWithMakes(props)} />,
  preload: (store, props) => {
    const params = getParamsWithMakes(props);
    const query = getQuery(props.location);
    const { sorting, pagesize, pagenum, aspectname, aspecttopic, rating, polarity } = query;
    const aspect = { displayName: aspectname, topic: aspecttopic };

    const preloader = new VehiclePreloader(store, { ...props, params }, { isSubpage: false });
    const { mobile } = store.getState();

    preloader.resolve('location', VisitorModel);

    preloader.set(ConsumerReviewsPaths.getVehicleFilterPath(), ConsumerReviewsModel, { params });
    preloader.set(ConsumerReviewsPaths.getConsumerReviewsFilterPath(), ConsumerReviewsModel, {
      sorting: sorting && SORT_BY.some(({ value }) => sorting === value) ? sorting : DEFAULT_SORTING,
      pagenum: pagenum ? parseInt(pagenum, 10) : DEFAULT_PAGE_NUM,
      pagesize: pagesize ? parseInt(pagesize, 10) : DEFAULT_PAGE_SIZE,
      aspect: aspect.displayName ? aspect : null,
      polarity: !polarity ? null : polarity,
      rating: rating ? parseInt(rating, 10) : DEFAULT_RATING,
    });
    preloader.set(URL_CONTEXT_PATH, UrlModel, {
      urlPattern: getCoreUrlPattern(params.year, params.submodel, 'consumer-reviews'),
    });

    return preloader.load().then(modelState => {
      const vehicle = modelState.get(buildVehiclePath(params), VehicleModel);
      const vehicleParams = getParamsFromVehicle(vehicle);
      preloader.resolve(buildMakeModelDefaultYear(params), VehicleModel);
      preloader.resolve(ConsumerReviewsPaths.getStylesPath(), ConsumerReviewsModel);
      preloader.resolve(ConsumerReviewsPaths.getConsumerReviewsPath(), ConsumerReviewsModel);

      // hero photo
      preloader.resolve(buildReviewPath(params), EditorialReviewModel);

      preloader.resolve(getPageLinksPath(), LinkWidgetsModel);

      const isUsed = isUsedPubState(vehicle.pubStates);
      const isPreprod = isPreProd(vehicle.pubStates);
      const isInternational = isUnknownZip(modelState.get('location', VisitorModel));

      store.dispatch(
        legacyPageUpdate({
          pageName: `${isUsed ? 'used' : 'new'}_${PAGE_NAME}`,
          categoryName: `${isUsed ? 'used' : 'new'}_${PAGE_CATEGORY}`,
        })
      );

      preloader.set('searchResultsFilter', InventoryModel, {
        ...getUnknownLocationSearchFilter(isInternational),
        ...getInitialSearchFilter({ makeSlug: params.make, modelSlug: params.model, year: params.year }),
        ...getFilterFromLocation(query),
        pageSize: MINI_INVENTORY_COUNT[!!mobile],
      });
      preloader.resolve('searchResults', InventoryModel);

      if (params.styleId) {
        preloader.resolve(buildVehicleStylePath(params), VehicleModel);
        preloader.resolve(
          ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPathByStyleId(params.styleId),
          ConsumerReviewsModel
        );
      } else {
        // refactors here for ratings count needed
        preloader.resolve(
          params.submodel
            ? ConsumerReviewsPaths.buildSubmodelConsumerReviewsRatingsCountPathFromVehicle(vehicle)
            : ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPathFromVehicle(vehicle),
          ConsumerReviewsModel
        );
        // refactors here for topics needed
        preloader.resolve(ConsumerReviewsPaths.buildConsumerReviewsAspectsPath(vehicle), ConsumerReviewsModel);
      }

      const vehiclePageContext = modelState.get('vehicle', PageModel);
      setAdsListToPreload(
        preloader,
        store.getState(),
        [INCENTIVES_AD.AD_NAME, BUILD_PRICE_AD.AD_NAME],
        vehiclePageContext
      );

      preloader.resolve(
        buildAllPhotosPath({
          ...vehicleParams,
          pageNum: 1,
          submodel: vehicle.submodels.name,
          imageSize: 600,
        }),
        PhotoflipperModel
      );

      if (isPreprod) {
        return preloader.load().then(modelStateInstance => {
          const consumerReviews = modelStateInstance.get(
            ConsumerReviewsPaths.getConsumerReviewsPath(),
            ConsumerReviewsModel
          );

          store.dispatch(
            legacyPageUpdate({
              pageName: `preprod_${PAGE_NAME}`,
              categoryName: `preprod_${PAGE_CATEGORY}`,
            })
          );

          if (!get(consumerReviews, 'totalReviews')) {
            throw create404Error("Preprod vehicle doesn't have reviews for current generation");
          }

          return preloader.load();
        });
      }

      if (isUsed) {
        preloader.resolve(buildMMYSubmodelTypeAmountsPath({ ...params, isUsed }), InventoryAggregateModel);
        preloader.resolve(getUsedBaseStylePath(vehicleParams), VehicleModel);

        // Srp Photo Box
        if (!mobile) {
          preloader.resolve(
            InventoryPaths.buildMMYValidPhotosPath({
              makeName: vehicle.make.name,
              modelName: vehicle.model.name,
              year: vehicle.year,
            }),
            InventoryModel
          );
        }

        return preloader.load().then(modelStateInstance => {
          const submodelAmounts = modelStateInstance.get(
            buildMMYSubmodelTypeAmountsPath({ ...vehicleParams, isUsed }),
            InventoryAggregateModel
          );
          const hasPriceRange = get(submodelAmounts, `priceRanges["${vehicle.submodels.slug}"]`);

          if (isInternational || !hasPriceRange) {
            const usedBaseStyleId = modelStateInstance.get(
              `${getUsedBaseStylePath(vehicleParams)}.usedDefaultStyle.id`,
              VehicleModel
            );

            if (usedBaseStyleId) {
              preloader.resolve(buildUsedTmvPricingPath(usedBaseStyleId), VehicleModel);
            }
          }

          // Srp Photo Box
          const validPhotos = modelStateInstance.get(
            InventoryPaths.buildMMYValidPhotosPath({
              makeName: vehicle.make.name,
              modelName: vehicle.model.name,
              year: vehicle.year,
            }),
            InventoryModel
          );
          if (!mobile && get(validPhotos, 'length', 0) < 4) {
            preloader.resolve(
              buildAllPhotosPath({
                ...vehicleParams,
                submodel: vehicle.submodels.name,
                pageNum: 1,
                imageSize: 175,
              }),
              PhotoflipperModel
            );
          }

          store.dispatch(setCriticalCss({ isUsed }));

          return preloader.load();
        });
      }

      // new only
      preloader.resolve(buildRecommendedTrimPath(params), EditorialReviewModel);
      preloader.resolve(buildTrimSummariesPath(vehicleParams), InventoryCoreConfigurationsModel);
      preloader.resolve(buildPopularStylesPath(vehicleParams), VehicleModel);
      if (!mobile) {
        preloader.resolve(FeatureSpecsPaths.buildPartialFeaturesPath(vehicleParams), VehicleModel);
      }

      return preloader.load();
    });
  },
  seo: (state, props) => {
    const params = getParamsWithMakes(props);
    const { pagenum = 1, pagesize = DEFAULT_PAGE_SIZE } = getQuery(props.location);
    const modelState = getModelState(state);
    const makeModelSubmodelYear = modelState.get(buildVehiclePath(params), VehicleModel);
    const style = params.styleId && modelState.get(buildVehicleStylePath(params), VehicleModel);
    const consumerReviews = modelState.get(ConsumerReviewsPaths.getConsumerReviewsPath(), ConsumerReviewsModel);
    const defaultYear = modelState.get(buildMakeModelDefaultYear(params), VehicleModel);

    const totalNumber = get(consumerReviews, 'totalReviews', '');
    const totalPages = Math.ceil(totalNumber / pagesize);
    const displayVehicle = buildSpecificStyleDisplayVehicle({ makeModelSubmodelYear, params, style });
    const displayVehicleNoPubState = buildSpecificStyleDisplayVehicle({
      makeModelSubmodelYear,
      params,
      style,
      usedPrefix: '',
    });
    const displayVehicleShort = buildDisplayVehicle({
      makeModelSubmodelYear,
      params,
      usedPrefix: '',
      showMake: false,
      showSubmodel: false,
      disableTrademarkCharacter: true,
    });

    const paginatedTitle = `${displayVehicle} Consumer Reviews - Page ${pagenum} | Edmunds`;
    const firstPageTitle = `${displayVehicle} Consumer Reviews - ${totalNumber} Car Reviews | Edmunds`;
    const title = pagenum > 1 ? paginatedTitle : firstPageTitle;
    const description = `View all ${totalNumber} consumer vehicle reviews for the ${displayVehicleNoPubState} on Edmunds, or submit your own review of the ${displayVehicleShort}.`;
    const seoUrls = totalPages ? getSeoUrlsByLocation(state.pageContext.location, { totalPages }) : {};

    const seoObject = {
      title,
      description,
      ...seoUrls,
      jsonld: [
        getBreadcrumbSchema(
          buildCoreBreadcrumbs({
            makeModelSubmodelYear,
            params,
            defaultYear,
            pageDisplayName: PAGE_DISPLAY_NAME,
            urlPath: getUrlPathFromParams(params, URL_PATH),
            includeLastLink: true,
          })
        ),
      ],
    };
    seoObject.jsonld.push(
      getCorePageCarSchema({
        makeModelSubmodelYear,
        consumerReviews,
        seoObject,
      })
    );

    return seoObject;
  },
  options: {
    hasVehicleContext: true,
    dontResetVehicleContextOnUnload: true,
  },
};

export default pageDefinition(pageOptions);
